import { Icon, usePrompt } from '@ur-solutions/react-components'
import type { CellProps } from '@/components_deprecated'
import { Button, Cell } from '@/components_deprecated'
import { parseISO } from 'date-fns'
import { useSalaryDayMutations } from '@/modules/activities/mutations.hooks'
import type { SalaryDayNode } from '@/modules/activities/types.graphql'
import type { MouseEventHandler } from 'react'
import { format } from '@/utils/date-fns'
import { useParam, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { SalaryDayForm } from './SalaryDayPrompt'
import { SalaryDayPrompt } from './SalaryDayPrompt'
import { useTranslate } from '@ur-solutions/react-hooks'

interface SalaryDayProps {
  salaryDay: Pick<
    SalaryDayNode,
    'isManuallyEdited' | 'partOfDay' | 'salaryCode' | 'date'
  > | null
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLDivElement>
  canEdit?: boolean | null
}

const PartOfDayMapping = {
  1: '1',
  0.75: '3/4',
  0.5: '1/2',
  0.25: '1/4',
} as { [key: number]: string }
export const SalaryDay: React.JSX.CFC<SalaryDayProps> = ({
  salaryDay,
  canEdit,
  ...props
}) => {
  const translations = useTranslate({
    manuallyEditedTitle: 'common.manually-edited',
  })
  const partOfDay = salaryDay?.partOfDay ?? 0
  if (salaryDay) {
    const isManuallyEdited = salaryDay.isManuallyEdited
    const title = isManuallyEdited ? translations.manuallyEditedTitle : ''
    if (Object.keys(PartOfDayMapping).includes(partOfDay.toString()))
      return (
        <div onClick={evt => props.onClick?.(evt)} title={title}>
          {PartOfDayMapping[partOfDay]} {salaryDay.salaryCode?.name}
          {isManuallyEdited && '*'}
        </div>
      )
    else if (partOfDay > 0)
      return (
        <div onClick={evt => props.onClick?.(evt)} title={title}>
          {salaryDay.partOfDay} {salaryDay.salaryCode?.name}
          {isManuallyEdited && '*'}
        </div>
      )
  }

  if (canEdit)
    return (
      <Button
        padding="0.25rem 0.75rem !important"
        height="auto"
        onClick={evt => {
          evt.stopPropagation()
          props.onClick?.(evt)
        }}
      >
        <Icon icon="plus" margin="0" />
      </Button>
    )

  return <div onClick={evt => props.onClick?.(evt)}></div>
}
interface SalaryDayCellProps extends SalaryDayProps, CellProps {
  subColumn: boolean
  date?: string | null
  onClick?: () => void
}
export const SalaryDayCell: React.FC<SalaryDayCellProps> = ({
  narrow = true,
  centered = true,
  subColumn = false,
  salaryDay,
  date = null,
  ...props
}) => {
  const userId = useParam('userId')

  const addPrompt = usePrompt()
  const { hasPermissions, hasPermissionsAndMe } = usePermissions()
  const mutations = useSalaryDayMutations()

  if (subColumn) return <td></td>

  const canEdit =
    hasPermissions(PERMISSIONS.activities.change.driveractivity) ||
    hasPermissionsAndMe(
      { id: userId },
      PERMISSIONS.activities.change.driverdiet
    )

  async function handleAddSalaryDay() {
    const { data } = await addPrompt<SalaryDayForm | null>(resolve => (
      <SalaryDayPrompt
        initialData={{
          userId: userId,
          partOfDay: salaryDay?.partOfDay ?? 1,
          salaryCodeId: salaryDay?.salaryCode?.id ?? null,
        }}
        onSubmit={resolve}
      />
    ))
    if (data === null || !data.salaryCodeId) return

    void mutations.updateOrCreateSalaryDay({
      variables: {
        input: {
          userId: userId,
          partOfDay: data.partOfDay,
          date: date
            ? date
            : salaryDay?.date
            ? format(parseISO(salaryDay.date), 'yyyy-MM-dd')
            : null,
          salaryCodeId: data.salaryCodeId,
        },
      },
    })
  }

  const handleClick: MouseEventHandler<HTMLTableCellElement> =
    salaryDay && canEdit
      ? event => {
          event.stopPropagation()
          void handleAddSalaryDay()
        }
      : _event => void 0

  return (
    <Cell onClick={handleClick} narrow={narrow} centered={centered}>
      <SalaryDay
        salaryDay={salaryDay}
        onClick={handleAddSalaryDay}
        {...props}
        canEdit={canEdit}
      />
    </Cell>
  )
}
